/**
 * Copy for the agency's scanner roster (USR-16, UPL-13, **ADR-0098**).
 *
 * **A read-only screen has to explain itself twice**, which is what most of this
 * file is. An admin arriving here will look for an Add and a Revoke and find
 * neither, and a screen that simply omits them reads as broken rather than as
 * deliberate. So it says who issues a scanner, and — the sentence that matters —
 * what to do about one that has been compromised, since the answer is a
 * telephone call and nobody guesses that.
 *
 * **An absence is stated, never implied.** "Never uploaded" and "no scanner has
 * been issued" are different facts from an empty cell, and both are said.
 *
 * Voice: `general.copy.ts`'s register. Sentence case, no persuasion, and no
 * apology for the missing controls — they are a release boundary, not a defect.
 */
export const SCANNERS_COPY = {
  title: 'Scanners',
  summary: 'The file servers that upload into your spaces, and what each one sends as.',

  /**
   * Said above the table, before an admin goes looking for a button. It names
   * Blue Partner rather than "the operator", because that is who they would
   * telephone.
   */
  managedBy:
    'Blue Partner issues and revokes these. To add one, or to stop one that has been compromised, contact Blue Partner.',

  loading: 'Reading scanners',
  failed: 'Could not read your scanners.',
  retry: 'Try again',

  /**
   * The empty state. It says what a scanner *is* rather than only that there
   * are none, because most agencies will never have one and an admin who lands
   * here should not have to go and look the feature up.
   */
  empty:
    'No scanner has been issued to your agency. A scanner is a file server that uploads into one of your spaces without anybody signing in.',

  table: {
    /** The table's accessible name. */
    label: 'Scanners issued to your agency',
    scanner: 'Scanner',
    device: 'Box',
    uploadsAs: 'Uploads as',
    destination: 'Sends to',
    lastUsed: 'Last upload',
    status: 'Status',
  },

  /** A box wired up and never used looks exactly like this. */
  neverUsed: 'Never',
  active: 'Active',
  revoked: 'Revoked',

  /**
   * Under the table. Points at the two places the same facts appear from a
   * different angle — the log records when a scanner was issued and revoked, and
   * Files shows what it has actually sent.
   */
  seeAlso:
    'Every file a scanner sends appears in Files, and the Audit log records when each was issued.',
} as const;
